import React, { useEffect, useRef } from 'react'
import { Modal as StyleguideModal, ModalModel } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

import * as SC from './ModalStyles'
import type { ModalLayout } from './ModalStyles'
import { useRegisterModalOpen } from './ModalOpenContext'

type StyleguideModalProps = React.ComponentProps<typeof StyleguideModal>

export type ModalProps = Omit<StyleguideModalProps, 'withFocusTrap' | 'fullscreenOnMobile'> & {
	/**
	 * Default false. Enable only after verifying that all interactive content,
	 * including portaled dropdowns and third-party overlays, remains accessible.
	 */
	withFocusTrap?: boolean
	/**
	 * Controls fullscreen behavior instead of styleguide `fullscreenOnMobile`.
	 * - default — no fullscreen
	 * - fullScreenOnMobile — styleguide fullscreenOnMobile only
	 * - fullScreenOnMobileCustom — styleguide fullscreenOnMobile + shared CSS overrides
	 */
	layout?: ModalLayout
	/** Hide the styleguide default close button (use a custom close in content). Default false. */
	hideDefaultCloseButton?: boolean
	/** Desktop max-width for `fullScreenOnMobileCustom`. Default 360. */
	desktopMaxWidth?: number
	/**
	 * Accessible name for the dialog. Applied to role="dialog" because the styleguide Modal
	 * does not set aria-labelledby / aria-label on the dialog element.
	 * Ignored when `ariaLabelledBy` is set (prefer visible title via labelledby).
	 */
	ariaLabel?: string
	/** Id of the visible title element that names the dialog. Takes precedence over `ariaLabel`. */
	ariaLabelledBy?: string
	/** Id of the element that describes the dialog. */
	ariaDescribedBy?: string
}

const Modal = (props: ModalProps) => {
	const {
		show,
		withFocusTrap = false,
		disableCloseOnEscape,
		disableCloseButton = false,
		layout = 'default',
		hideDefaultCloseButton = false,
		desktopMaxWidth = 360,
		ariaLabel,
		ariaLabelledBy,
		ariaDescribedBy,
		children,
		...rest
	} = props

	const containerRef = useRef<HTMLDivElement>(null)
	const { formatMessage } = useIntl()
	const closeButtonLabel = formatMessage({ id: 'Close modal', defaultMessage: 'Close' })
	const hasDefaultCloseButton = Boolean(show && !hideDefaultCloseButton && !disableCloseButton)

	useRegisterModalOpen(show)

	useEffect(() => {
		const dialog = containerRef.current?.querySelector<HTMLElement>('[role="dialog"]')

		if (!dialog) {
			return
		}

		if (show && ariaLabelledBy) {
			dialog.setAttribute('aria-labelledby', ariaLabelledBy)
			dialog.removeAttribute('aria-label')
		} else if (show && ariaLabel) {
			dialog.setAttribute('aria-label', ariaLabel)
			dialog.removeAttribute('aria-labelledby')
		} else {
			dialog.removeAttribute('aria-label')
			dialog.removeAttribute('aria-labelledby')
		}

		if (show && ariaDescribedBy) {
			dialog.setAttribute('aria-describedby', ariaDescribedBy)
		} else {
			dialog.removeAttribute('aria-describedby')
		}

		if (!hasDefaultCloseButton) {
			return
		}

		// Styleguide ModalContainer children: [BackButton?], CloseButton, content.
		// Same `dialog > button` target as ModalStyles; close is always the last direct child button.
		const closeButton = dialog.querySelector<HTMLButtonElement>(':scope > button:last-of-type')

		if (closeButton) {
			closeButton.setAttribute('aria-label', closeButtonLabel)
			// Styleguide incorrectly sets aria-expanded on the close control.
			closeButton.removeAttribute('aria-expanded')
		}
	}, [show, ariaLabel, ariaLabelledBy, ariaDescribedBy, hasDefaultCloseButton, closeButtonLabel])

	const fullscreenOnMobile = layout === 'fullScreenOnMobile' || layout === 'fullScreenOnMobileCustom'
	// Styleguide FocusTrap has no inactive mode — only enable it while the modal is shown.
	// Closed dialogs (e.g. always-mounted ReservationBookingDialog) would otherwise activate
	// with zero tabbable nodes and throw.
	const isFocusTrapEnabled = Boolean(show && withFocusTrap)

	return (
		<SC.ModalContainer ref={containerRef} $layout={layout} $hideDefaultCloseButton={hideDefaultCloseButton} $desktopMaxWidth={desktopMaxWidth}>
			<StyleguideModal
				show={show}
				withFocusTrap={isFocusTrapEnabled}
				disableCloseOnEscape={disableCloseOnEscape}
				disableCloseButton={disableCloseButton}
				fullscreenOnMobile={fullscreenOnMobile}
				aria-label={ariaLabelledBy ? undefined : ariaLabel}
				aria-labelledby={ariaLabelledBy}
				aria-describedby={ariaDescribedBy}
				{...rest}
			>
				{children}
			</StyleguideModal>
		</SC.ModalContainer>
	)
}

export type { ModalLayout }
export { ModalModel }
export { ModalOpenProvider, useIsAnyModalOpen, useRegisterModalOpen } from './ModalOpenContext'
export default Modal
